import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import PdfViewer from '@/components/PdfViewer';
import Seo from '@/components/Seo';
import { getCourse } from '@/data/catalog';
import { useAuth } from '@/hooks/useAuth';
import { downloadCertificate } from '@/lib/certificate';
import { fetchMyEnrollment, type CourseEnrollment } from '@/lib/enrollments';
import { fetchCourseMaterials, materialForModule, type ModuleMaterial } from '@/lib/materials';
import { applyRemoteProgress, enrollInCourse, fetchRemoteProgress, getEnrollment, markModuleComplete, progressPercent, syncCourseProgress } from '@/lib/progress';

function Classroom() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const course = getCourse(slug);
  const { isAuthenticated, user } = useAuth();
  const [active, setActive] = useState(0);
  const [slide, setSlide] = useState(0);
  const [tick, setTick] = useState(0);
  const [request, setRequest] = useState<CourseEnrollment | undefined>();
  const [materials, setMaterials] = useState<ModuleMaterial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userId = user?.userId;
    const courseId = course?.id;
    if (!userId || !courseId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchMyEnrollment(userId, courseId).
    then((record) => {
      if (cancelled) return;
      setRequest(record);
      if (record?.status === 'approved') {
        enrollInCourse(userId, {
          courseId,
          fullName: record.fullName || user?.userName || '',
          email: record.email || user?.email || '',
          phone: record.phone || '',
          note: record.note || ''
        });
      }
    }).
    catch((err) => {
      console.error('No se pudo consultar el acceso al aula:', err);
    }).
    finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.userId, user?.userName, user?.email, course?.id]);

  useEffect(() => {
    const courseId = course?.id;
    if (!courseId) return;
    let cancelled = false;
    fetchCourseMaterials(courseId).
    then((list) => {
      if (!cancelled) setMaterials(list);
    }).
    catch((err) => {
      console.error('No se pudieron cargar los materiales del aula:', err);
    });
    return () => {
      cancelled = true;
    };
  }, [course?.id]);

  useEffect(() => {
    const userId = user?.userId;
    const courseId = course?.id;
    const courseSlug = course?.slug;
    if (!userId || !courseId || !courseSlug) return;
    let cancelled = false;
    fetchRemoteProgress(userId, courseId).
    then((remote) => {
      if (cancelled || !remote) return;
      applyRemoteProgress(userId, courseId, remote.completedModules, remote.completedAt || null);
      setTick((value) => value + 1);
    }).
    catch((err) => {
      console.error('No se pudo cargar el avance guardado:', err);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.userId, course?.id, course?.slug]);

  const enrollment = useMemo(() => {
    void tick;
    if (!user?.userId || !course) return undefined;
    return getEnrollment(user.userId, course.id);
  }, [user?.userId, course, tick]);

  if (!course) {
    return <Navigate to="/cursos" replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to={`/acceso?next=/aula/${course.slug}`} replace />;
  }

  if (loading) {
    return (
      <section className="mx-auto max-w-3xl px-5 py-20">
        <p className="text-sm text-[#1E3A5F]/65">Comprobando tu acceso…</p>
      </section>);

  }

  if (!request || request.status === 'pending') {
    return (
      <section className="mx-auto max-w-3xl px-5 py-20">
        <h1 className="font-['Fraunces'] text-3xl">Acceso pendiente</h1>
        <p className="mt-4 text-sm leading-6 text-[#1E3A5F]/70">
          Tu solicitud está en revisión. Un administrador debe aceptarte en el curso o darte acceso a los documentos.
        </p>
        <Link to={`/cursos/${course.slug}`} className="mt-6 inline-block text-sm underline">
          Volver a la ficha
        </Link>
      </section>);

  }

  if (request.status === 'rejected') {
    return (
      <section className="mx-auto max-w-3xl px-5 py-20">
        <h1 className="font-['Fraunces'] text-3xl">Solicitud no aceptada</h1>
        <p className="mt-4 text-sm leading-6 text-[#1E3A5F]/70">
          Esta inscripción no tiene acceso al aula. Si lo necesitas, escribe a soporte.
        </p>
        <Link to={`/cursos/${course.slug}`} className="mt-6 inline-block text-sm underline">
          Volver a la ficha
        </Link>
      </section>);

  }

  if (!request.documentsAccess) {
    return (
      <section className="mx-auto max-w-3xl px-5 py-20">
        <p className="text-xs uppercase tracking-[0.28em] text-[#C41E3A]">Aceptado en el curso</p>
        <h1 className="mt-3 font-['Fraunces'] text-3xl">{course.title}</h1>
        <p className="mt-4 text-sm leading-6 text-[#1E3A5F]/70">
          Ya formas parte del curso. Los documentos y presentaciones se desbloquearán cuando un administrador te dé acceso.
        </p>
        <Link to="/panel" className="mt-6 inline-block text-sm underline">
          Ir al panel
        </Link>
      </section>);

  }

  const module = course.modules[active];
  const currentSlide = module?.slides[slide];
  const material = module ? materialForModule(materials, module.id) : undefined;
  const percent = progressPercent(enrollment, course.modules.length);
  const done = Boolean(enrollment?.completedAt);

  const completeModule = () => {
    if (!user?.userId || !module) return;
    const next = markModuleComplete(user.userId, course.id, module.id, course.modules.length);
    setTick((value) => value + 1);
    if (next) {
      syncCourseProgress(user.userId, course.id, course.slug, next.completedModules, next.completedAt).catch((err) => {
        console.error('No se pudo actualizar el avance del curso en el servidor:', err);
      });
    }
  };

  return (
    <>
      <Seo title={`Aula · ${course.title}`} description={`Presentaciones y módulos de ${course.title}.`} />
      <section className="mx-auto max-w-6xl px-5 py-10 lg:px-8">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-[#1E3A5F]/70 underline-offset-4 hover:underline">

          <span aria-hidden="true">←</span> Volver al curso
        </button>
        <p className="mt-4 text-xs uppercase tracking-[0.28em] text-[#C41E3A]">Aula</p>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-['Fraunces'] text-3xl">{course.title}</h1>
          <p className="text-sm text-[#1E3A5F]/60">Progreso {percent}%</p>
        </div>
        <div className="mt-3 h-1 bg-[#1E3A5F]/10" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-full bg-[#C41E3A]" style={{ width: `${percent}%` }} />
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-[240px_1fr]">
          <nav aria-label="Módulos">
            <ol className="space-y-2">
              {course.modules.map((item, index) => {
                const completed = enrollment?.completedModules.includes(item.id);
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setActive(index);
                        setSlide(0);
                      }}
                      className={`w-full border px-3 py-2 text-left text-sm ${
                      index === active ? 'border-[#1E3A5F] bg-[#1E3A5F] text-[#F4EFE6]' : 'border-[#1E3A5F]/15'}`
                      }>

                      <span className="block text-xs opacity-70">Sesión {index + 1}</span>
                      {item.title}
                      {completed ? <span className="ml-2 text-xs">· hecha</span> : null}
                    </button>
                  </li>);

              })}
            </ol>
          </nav>

          <div>
            <div className="min-h-[320px] border border-[#1E3A5F]/15 bg-[#EDE6D9] p-8">
              {material?.pdfUrl ?
              <PdfViewer src={material.pdfUrl} title={`Presentación · ${module.title}`} /> :

              <>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#C41E3A]">
                    Presentación · {slide + 1} / {module.slides.length}
                  </p>
                  <h2 className="mt-6 font-['Fraunces'] text-3xl">{currentSlide?.title}</h2>
                  <p className="mt-6 max-w-2xl text-base leading-7 text-[#1E3A5F]/75">{currentSlide?.body}</p>
                  <p className="mt-4 text-xs text-[#1E3A5F]/45">
                    Aún no se ha subido la presentación en PDF de esta sesión.
                  </p>
                </>
              }
              {material?.geeUrl || material?.zoomUrl ?
              <div className="mt-6 space-y-2 border-t border-[#1E3A5F]/10 pt-4">
                  {material?.geeUrl ?
                <p className="text-sm">
                      <span className="text-xs uppercase tracking-[0.18em] text-[#C41E3A]">Liga de interés o códigos GEE</span>
                      <a
                    href={material.geeUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 block break-all text-[#C41E3A] underline underline-offset-4">

                        {material.geeUrl}
                      </a>
                    </p> :
                null}
                  {material?.zoomUrl ?
                <p className="text-sm">
                      <span className="text-xs uppercase tracking-[0.18em] text-[#C41E3A]">Enlace de Zoom</span>
                      <a
                    href={material.zoomUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 block break-all text-[#C41E3A] underline underline-offset-4">

                        {material.zoomUrl}
                      </a>
                    </p> :
                null}
                </div> :
              null}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={slide === 0}
                onClick={() => setSlide((value) => Math.max(0, value - 1))}
                className="border border-[#1E3A5F]/20 px-3 py-2 text-sm disabled:opacity-40">

                Anterior
              </button>
              <button
                type="button"
                disabled={slide >= module.slides.length - 1}
                onClick={() => setSlide((value) => Math.min(module.slides.length - 1, value + 1))}
                className="border border-[#1E3A5F]/20 px-3 py-2 text-sm disabled:opacity-40">

                Siguiente
              </button>
              <button
                type="button"
                onClick={completeModule}
                className="bg-[#1E3A5F] px-3 py-2 text-sm text-[#F4EFE6]">

                Marcar sesión como hecha
              </button>
            </div>
            {done ?
            <div className="mt-8 border border-[#C41E3A]/30 bg-[#F4EFE6] p-5">
                <p className="font-['Fraunces'] text-xl">Curso completado</p>
                <p className="mt-2 text-sm text-[#1E3A5F]/70">Ya puedes descargar tu certificado.</p>
                <button
                type="button"
                onClick={() =>
                downloadCertificate({
                  studentName: enrollment?.fullName || user?.userName || 'Estudiante',
                  courseTitle: course.title,
                  completedAt: enrollment?.completedAt || new Date().toISOString()
                })
                }
                className="mt-4 border border-[#1E3A5F] px-4 py-2 text-sm">

                  Descargar certificado
                </button>
              </div> :
            null}
            <Link to="/panel" className="mt-6 inline-block text-sm underline">
              Volver al panel
            </Link>
          </div>
        </div>
      </section>
    </>);

}

export default Classroom;